import { ClassData } from '../types';

export const formatTime = (timeStr: string): string => {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':').map(Number);
  if (isNaN(hours)) return '';
  
  const h = hours;
  const m = minutes;

  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

export const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const generateGoogleCalendarUrl = (classDetails: ClassData) => {
  const formatGoogleCalendarTime = (date: string, time: string): string => {
    const datePart = date.replace(/-/g, '');
    const timePart = time.replace(/:/g, '') + '00';
    return datePart + 'T' + timePart;
  };

  const start = formatGoogleCalendarTime(classDetails.date, classDetails.startTime);
  const end = formatGoogleCalendarTime(classDetails.date, classDetails.endTime);
  const title = encodeURIComponent(`${classDetails.name} with Kat`);
  const details = encodeURIComponent(
    `You are booked for ${classDetails.name} at ${classDetails.studio}.` + 
    (classDetails.description ? `\n\nAbout this class:\n${classDetails.description}` : '')
  );
  const location = encodeURIComponent(classDetails.studio);
  
  return `https://www.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}&location=${location}`;
};

export const generateICS = (classDetails: ClassData) => {
  const desc = classDetails.description ? classDetails.description.replace(/\n/g, '\\n') : 'Yoga class with Kat.';
  const text = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Yoga Classes with Kat//EN
BEGIN:VEVENT
SUMMARY:${classDetails.name}
DTSTART:${classDetails.date.replace(/-/g, '')}T${classDetails.startTime.replace(/:/g, '')}00
DTEND:${classDetails.date.replace(/-/g, '')}T${classDetails.endTime.replace(/:/g, '')}00
DESCRIPTION:${desc}
LOCATION:${classDetails.studio || 'Roots & Wings'}
END:VEVENT
END:VCALENDAR`;
  
  const blob = new Blob([text], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'yoga-class.ics');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const getVenmoUrl = (cost: number | null, className: string, venmoUsername: string) => {
    const note = encodeURIComponent(className || 'Yoga Class');
    
    if (isMobileDevice()) {
        let url = `venmo://paycharge?txn=pay&recipients=${venmoUsername}&note=${note}`;
        if (cost && cost > 0) url += `&amount=${cost}`;
        return url;
    }

    let url = `https://venmo.com/?txn=pay&recipients=${venmoUsername}&note=${note}`;
    if (cost && cost > 0) {
        url += `&amount=${cost}`;
    }
    return url;
};

export const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/**
 * Generate Venmo URL for batch checkout with multiple classes.
 * 
 * @param totalCost - Sum of all class costs
 * @param itemCount - Number of classes in the cart
 * @param venmoUsername - Venmo recipient username
 * @returns Venmo deep link or web URL
 */
export const getBatchVenmoUrl = (
  totalCost: number | null, 
  itemCount: number, 
  venmoUsername: string
): string => {
  const note = encodeURIComponent(`Yoga Classes (${itemCount} ${itemCount === 1 ? 'class' : 'classes'})`);
  
  if (isMobileDevice()) {
    let url = `venmo://paycharge?txn=pay&recipients=${venmoUsername}&note=${note}`;
    if (totalCost && totalCost > 0) url += `&amount=${totalCost}`;
    return url;
  }

  let url = `https://venmo.com/?txn=pay&recipients=${venmoUsername}&note=${note}`;
  if (totalCost && totalCost > 0) {
    url += `&amount=${totalCost}`;
  }
  return url;
};
